/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { useState } from "react";
import { FormBottomPropsActionsProps } from "../../types";
import { Box } from "../Box";
import { Button } from "../Button";

export const FormFooterActions = (props: FormBottomPropsActionsProps & { isDisabled?: boolean }) => {
  const {
    activeFrom,
    formList,
    formMethods,
    hideActionButtons,
    prevButtonProps,
    nextButtonProps,
    onPreviousForm,
    onNextForm,
    onSubmit,
    submitText,
    otherButtons = [],
    isDisabled, // <-- new prop
  } = props;

  const [submitting, setSubmitting] = useState(false);
  const isLastForm = activeFrom === formList.length - 1;

  const handleSafeSubmit = formMethods.handleSubmit(async (data: any) => {
    try {
      setSubmitting(true);
      await onSubmit?.(data);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Box mt={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
      <Box display="flex" gap={1}>
        {prevButtonProps && !hideActionButtons && activeFrom > 0 && (
          <Button
            {...prevButtonProps}
            onClick={onPreviousForm}
            sx={{ minWidth: 120, ...prevButtonProps?.sx }}
          >
            {prevButtonProps.name}
          </Button>
        )}
      </Box>

      <Box display="flex" gap={1} flexWrap="wrap">
        {!isLastForm && nextButtonProps && !hideActionButtons && (
          <Button
            {...nextButtonProps}
            onClick={onNextForm}
            sx={{ minWidth: 120, ...nextButtonProps?.sx }}
          >
            {nextButtonProps.name}
          </Button>
        )}

        {onSubmit && (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={submitting || isDisabled} // <-- combine submitting & isDisabled
            onClick={handleSafeSubmit}
            sx={{ minWidth: 120 }}
          >
            {submitting ? "Submitting..." : submitText || "Submit"}
          </Button>
        )}

        {otherButtons.map((btn, i) => (
          <Button
            key={btn.name || `other-${i}`}
            {...btn}
            onClick={btn.handler}
            sx={{ minWidth: 120 }}
          >
            {btn.name}
          </Button>
        ))}
      </Box>
    </Box>
  );
};
